// Launching clips, in one place.
//
// Four surfaces now launch a slot clip — the seed page's Session grid, the
// player tab, the standalone play screen and the browser room's pads — and
// they must mean the same thing by it: a slot clip plays its OWN object (or
// its notes), never its lane's stem, because a stem is a render of the
// TIMELINE and a slot is not on the timeline. Launching a scene is a state of
// the whole Set, so a lane whose slot in that row is empty STOPS rather than
// carrying on — and it stops on the SAME line the row's clips start on, which
// is why a scene goes to the player as one call and not as a loop of them.
//
// Live's rules, in Live's words, once — `sceneLaunchPlan` and `clipAudition`
// are shared with the desktop grid for exactly the same reason.

import { useCallback, useEffect } from "react";
import { loopsOnLaunch, sceneLaunchPlan } from "@web-shared/lib/session-launch";
import { clipAuditionSource } from "@web-shared/lib/player-sources";
import type { Arrangement, Lane, LaneClip } from "@web-shared/lib/arrangement";
import type { ArrangementPlayerApi } from "@web-shared/hooks/useArrangementPlayer";
import type { ClipSource, MidiSource } from "@web-shared/lib/audio-engine";
import type { MidiStage } from "@web-shared/lib/midi-effects";

export interface LaunchControls {
  launchClip: (clip: LaneClip, lane: Lane) => void;
  launchScene: (row: number) => void;
  stopLane: (laneId: string) => void;
  stopAll: () => void;
  backToArrangement: (laneId?: string) => void;
}

export function useLaunchControls(
  player: ArrangementPlayerApi,
  arrangement: Arrangement | null,
  bpm: number,
  midiEffectsByLane: Record<string, readonly MidiStage[]> = {},
  /** How a clip's audio is fetched where the lane's object needs a URL of its
   * own (the browser room serves public blobs same-origin). */
  sourceFor?: (clip: LaneClip, lane: Lane) => ClipSource | MidiSource | null,
): LaunchControls {
  const { launchSlot, launchScene: launchSceneSources, stopSlot, stopAllSlots, backToArrangement, refreshSlots, sessionPlaying } = player;

  const sourceOf = useCallback(
    (clip: LaneClip, lane: Lane): ClipSource | MidiSource | null => {
      // A deactivated clip is silent in Live however it is started.
      if (clip.muted) return null;
      if (sourceFor) return sourceFor(clip, lane);
      return clipAuditionSource(lane, clip, bpm, { effects: midiEffectsByLane[lane.id] });
    },
    [sourceFor, bpm, midiEffectsByLane],
  );

  const launchClip = useCallback(
    (clip: LaneClip, lane: Lane) => {
      const src = sourceOf(clip, lane);
      if (!src) return; // nothing to sound; the grid draws it inert
      // A pad loops only if its clip can carry a loop — otherwise it is a
      // one-shot, as Live plays an unbraced clip.
      void launchSlot(lane.id, clip.id, src, loopsOnLaunch(clip));
    },
    [launchSlot, sourceOf],
  );

  const launchScene = useCallback(
    (row: number) => {
      if (!arrangement) return;
      const plan = sceneLaunchPlan(arrangement.lanes, row);
      const items = plan.launch
        .map(({ lane, clip }) => {
          const source = sourceOf(clip, lane);
          return source ? { laneId: lane.id, clipId: clip.id, source, loop: loopsOnLaunch(clip) } : null;
        })
        .filter(
          (i): i is { laneId: string; clipId: string; source: ClipSource | MidiSource; loop: boolean } => i !== null,
        );
      // A clip the plan counted on but whose object would not resolve leaves
      // its lane in the row's silence rather than playing on: the row is a
      // state of the Set, and a lane it does not fill is a lane it stops.
      const unsounded = plan.launch.filter(({ lane }) => !items.some((i) => i.laneId === lane.id)).map(({ lane }) => lane.id);
      void launchSceneSources(items, [...plan.stop, ...unsounded]);
    },
    [arrangement, sourceOf, launchSceneSources],
  );

  // An edit reaches a launched pad: whenever the arrangement changes, every
  // launched clip is rebuilt from it and the player swaps the ones that no
  // longer sound like their source, on one grid line. Nothing is relaunched
  // for a change that leaves a clip's sound alone.
  useEffect(() => {
    if (!arrangement) return;
    const items: Array<{ laneId: string; clipId: string; source: ClipSource | MidiSource; loop: boolean }> = [];
    for (const [laneId, clipId] of Object.entries(sessionPlaying)) {
      const lane = arrangement.lanes.find((l) => l.id === laneId);
      const clip = lane?.sessionClips.find((c) => c.id === clipId) ?? lane?.clips.find((c) => c.id === clipId);
      if (!lane || !clip) continue;
      const source = sourceOf(clip, lane);
      if (source) items.push({ laneId, clipId, source, loop: loopsOnLaunch(clip) });
    }
    if (items.length > 0) refreshSlots(items);
  }, [arrangement, sessionPlaying, sourceOf, refreshSlots]);

  return {
    launchClip,
    launchScene,
    stopLane: stopSlot,
    stopAll: stopAllSlots,
    backToArrangement,
  };
}
